import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type ReactQuill from 'react-quill';
import { toast } from 'react-hot-toast';
import {
  deleteCloudbridgeImage,
  extractImageSrcs,
  uploadCloudbridgeImage,
} from '@/services/cloudbridgeUpload';

type TrackedImage = {
  path: string;
  url: string;
};

interface UseCloudbridgeQuillImagesOptions {
  folder?: string;
}

export function useCloudbridgeQuillImages(options: UseCloudbridgeQuillImagesOptions = {}) {
  const folder = options.folder;
  const quillRef = useRef<ReactQuill | null>(null);
  const trackedRef = useRef<Map<string, TrackedImage>>(new Map());
  const uploadingRef = useRef(false);
  const uploadToastIdRef = useRef<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');

  const trackUpload = useCallback((url: string, path: string) => {
    if (!url || !path) return;
    trackedRef.current.set(url, { url, path });
  }, []);

  const deleteTrackedImage = useCallback(async (entry: TrackedImage) => {
    trackedRef.current.delete(entry.url);
    try {
      await deleteCloudbridgeImage(entry.path);
    } catch (error) {
      console.error('Failed to delete CloudBridge image:', error);
    }
  }, []);

  const syncTrackedImages = useCallback((html: string) => {
    const present = extractImageSrcs(html);
    const toDelete: TrackedImage[] = [];

    trackedRef.current.forEach((entry) => {
      if (!present.has(entry.url)) {
        toDelete.push(entry);
      }
    });

    toDelete.forEach((entry) => {
      void deleteTrackedImage(entry);
    });
  }, [deleteTrackedImage]);

  const releaseTracked = useCallback(() => {
    trackedRef.current.clear();
  }, []);

  const discardTracked = useCallback(() => {
    const entries = Array.from(trackedRef.current.values());
    trackedRef.current.clear();
    entries.forEach((entry) => {
      void deleteCloudbridgeImage(entry.path).catch((error) => {
        console.error('Failed to delete CloudBridge image on discard:', error);
      });
    });
  }, []);

  const insertUploadedImage = useCallback((url: string, path: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection(true);
    const index = range?.index ?? quill.getLength();
    quill.insertEmbed(index, 'image', url, 'user');
    quill.setSelection(index + 1, 0, 'user');
    trackUpload(url, path);
  }, [trackUpload]);

  const updateUploadToast = useCallback((progress: number) => {
    const message = `Uploading image… ${progress}%`;
    if (uploadToastIdRef.current) {
      toast.loading(message, { id: uploadToastIdRef.current });
      return;
    }
    uploadToastIdRef.current = toast.loading(message);
  }, []);

  const finishUploadToast = useCallback((type: 'success' | 'error', message: string) => {
    const toastId = uploadToastIdRef.current;
    uploadToastIdRef.current = null;

    if (type === 'success') {
      toast.success(message, toastId ? { id: toastId, duration: 2500 } : { duration: 2500 });
      return;
    }

    toast.error(message, toastId ? { id: toastId, duration: 5000 } : { duration: 5000 });
  }, []);

  const uploadAndInsertImage = useCallback(async (file: File) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setIsUploadingImage(true);
    setUploadProgress(0);
    setUploadFileName(file.name);
    updateUploadToast(0);

    try {
      const result = await uploadCloudbridgeImage(file, folder, (progress) => {
        setUploadProgress(progress);
        updateUploadToast(progress);
      });

      const path = result.nextcloudPath;
      if (!path) {
        throw new Error('Upload succeeded but CloudBridge path was missing');
      }

      insertUploadedImage(result.url, path);
      finishUploadToast('success', 'Image uploaded');
    } catch (error: any) {
      finishUploadToast('error', error.message || 'Failed to upload image');
    } finally {
      uploadingRef.current = false;
      setIsUploadingImage(false);
      setUploadProgress(0);
      setUploadFileName('');
    }
  }, [folder, finishUploadToast, insertUploadedImage, updateUploadToast]);

  const handleImageToolbar = useCallback(function imageHandler(this: { quill: any }) {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/gif');
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        void uploadAndInsertImage(file);
      }
    };
    input.click();
  }, [uploadAndInsertImage]);

  const attachPasteHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill || quill.root.dataset.cloudbridgePaste === 'true') return;

    quill.root.dataset.cloudbridgePaste = 'true';
    quill.root.addEventListener('paste', (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (!item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        event.preventDefault();
        void uploadAndInsertImage(file);
        return;
      }
    });
  }, [uploadAndInsertImage]);

  useEffect(() => {
    attachPasteHandler();
  });

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: handleImageToolbar,
      },
    },
    clipboard: {
      matchVisual: false,
    },
  }), [handleImageToolbar]);

  const handleBodyChange = useCallback((html: string, onChange: (value: string) => void) => {
    syncTrackedImages(html);
    onChange(html);
  }, [syncTrackedImages]);

  return {
    quillRef,
    quillModules,
    handleBodyChange,
    releaseTracked,
    discardTracked,
    syncTrackedImages,
    isUploadingImage,
    uploadProgress,
    uploadFileName,
  };
}
